/**
 * Comprehensive Error Management System
 * Provides centralized error handling, logging, and user feedback
 */

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  timestamp?: string;
  userAgent?: string;
  url?: string;
  additionalData?: Record<string, any>;
  critical?: boolean;
  originalError?: string;
  originalErrorId?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  success?: boolean;
  error?: string;
}

interface ErrorLog {
  id: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  error: Error | null;
  context: ErrorContext;
  timestamp: string;
  resolved: boolean;
}

type ErrorHandler = (error: Error, context: ErrorContext) => void;

class ErrorManager {
  private static instance: ErrorManager;
  private errorLogs: ErrorLog[] = [];
  private maxLogs = 100; // Prevent memory bloat
  private handlers: ErrorHandler[] = [];
  private silentMode = false;

  static getInstance(): ErrorManager {
    if (!ErrorManager.instance) {
      ErrorManager.instance = new ErrorManager();
    }
    return ErrorManager.instance;
  }

  constructor() {
    // Set up global error handlers
    if (typeof window !== 'undefined') {
      window.addEventListener('error', this.handleGlobalError.bind(this));
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    }
  }

  // ================ ERROR LOGGING ================

  /**
   * Log an error with full context
   */
  logError(
    error: Error | string, 
    context: ErrorContext = {},
    level: 'error' | 'warning' | 'info' = 'error'
  ): string {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    
    const errorLog: ErrorLog = {
      id: this.generateErrorId(),
      level,
      message: errorObj.message,
      error: errorObj,
      context: {
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        ...context
      },
      timestamp: new Date().toISOString(),
      resolved: false
    };

    // Add to logs (with rotation)
    this.errorLogs.unshift(errorLog);
    if (this.errorLogs.length > this.maxLogs) {
      this.errorLogs = this.errorLogs.slice(0, this.maxLogs);
    }

    // Console logging
    if (!this.silentMode) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'info';
      console[consoleMethod](`[ErrorManager] ${errorLog.message}`, {
        id: errorLog.id,
        context: errorLog.context,
        stack: errorObj.stack
      });
    }

    // Notify handlers
    this.handlers.forEach(handler => {
      try {
        handler(errorObj, errorLog.context);
      } catch (handlerError) {
        console.error('[ErrorManager] Handler error:', handlerError);
      }
    });

    // Send to monitoring service (in production)
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(errorLog);
    }

    return errorLog.id;
  }

  /**
   * Log critical errors that require immediate attention
   */
  logCritical(error: Error | string, context: ErrorContext = {}): string {
    const errorId = this.logError(error, { ...context, critical: true }, 'error');
    
    // In production, you might want to send alerts, emails, etc.
    if (process.env.NODE_ENV === 'production') {
      console.error('[CRITICAL ERROR]', { errorId, error, context });
    }
    
    return errorId;
  }

  /**
   * Log warnings for non-critical issues
   */
  logWarning(message: string, context: ErrorContext = {}): string {
    return this.logError(message, context, 'warning');
  }

  /**
   * Log info for debugging purposes
   */
  logInfo(message: string, context: ErrorContext = {}): string {
    return this.logError(message, context, 'info');
  }

  // ================ ERROR RECOVERY ================

  /**
   * Attempt to recover from an error
   */
  async recoverFromError(
    errorId: string, 
    recoveryFn: () => Promise<void> | void
  ): Promise<boolean> {
    const errorLog = this.errorLogs.find(log => log.id === errorId);
    if (!errorLog) {
      this.logWarning(`Attempted to recover from unknown error: ${errorId}`);
      return false;
    }

    try {
      await recoveryFn();
      errorLog.resolved = true;
      this.logInfo(`Successfully recovered from error: ${errorId}`, { 
        originalError: errorLog.message 
      });
      return true;
    } catch (recoveryError) {
      this.logError(recoveryError as Error, {
        component: 'ErrorManager',
        action: 'recoverFromError',
        originalErrorId: errorId
      });
      return false;
    }
  }

  // ================ GLOBAL HANDLERS ================

  private handleGlobalError(event: ErrorEvent): void {
    this.logError(new Error(event.message), {
      component: 'Global',
      action: 'windowError',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    this.logError(error, {
      component: 'Global',
      action: 'unhandledRejection'
    });
    
    // Prevent the default console error
    event.preventDefault();
  }

  // ================ UTILITIES ================

  /**
   * Add custom error handler
   */
  addHandler(handler: ErrorHandler): void {
    this.handlers.push(handler);
  }

  /**
   * Remove error handler
   */
  removeHandler(handler: ErrorHandler): void {
    const index = this.handlers.indexOf(handler);
    if (index > -1) {
      this.handlers.splice(index, 1);
    }
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number;
    byLevel: Record<string, number>;
    recent: number;
    resolved: number;
  } {
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000);
    
    const byLevel = this.errorLogs.reduce((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return {
      total: this.errorLogs.length,
      byLevel,
      recent: this.errorLogs.filter(log => 
        new Date(log.timestamp).getTime() > oneHourAgo
      ).length,
      resolved: this.errorLogs.filter(log => log.resolved).length
    };
  }

  /**
   * Clear resolved errors
   */
  clearResolvedErrors(): void {
    this.errorLogs = this.errorLogs.filter(log => !log.resolved);
  }

  /**
   * Get all error logs
   */
  getAllErrors(): ErrorLog[] {
    return [...this.errorLogs];
  }

  /**
   * Get errors by level
   */
  getErrorsByLevel(level: 'error' | 'warning' | 'info'): ErrorLog[] {
    return this.errorLogs.filter(log => log.level === level);
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async sendToMonitoring(errorLog: ErrorLog): Promise<void> {
    // In a real app, you'd send to your monitoring service
    // For now, just store in localStorage for debugging
    try {
      const existingErrors = JSON.parse(localStorage.getItem('error_monitoring') || '[]');
      existingErrors.unshift(errorLog);
      
      // Keep only last 50 errors in localStorage
      const trimmedErrors = existingErrors.slice(0, 50);
      localStorage.setItem('error_monitoring', JSON.stringify(trimmedErrors));
    } catch (storageError) {
      console.error('[ErrorManager] Failed to store error in localStorage:', storageError);
    }
  }

  /**
   * Enable/disable console logging
   */
  setSilentMode(silent: boolean): void {
    this.silentMode = silent;
  }
}

// ================ REACT INTEGRATION ================

/**
 * React hook for error management
 */
export const useErrorManager = () => {
  const errorManager = ErrorManager.getInstance();
  
  const logError = (error: Error | string, context?: ErrorContext) => {
    return errorManager.logError(error, context);
  };
  
  const logCritical = (error: Error | string, context?: ErrorContext) => {
    return errorManager.logCritical(error, context);
  };
  
  const logWarning = (message: string, context?: ErrorContext) => {
    return errorManager.logWarning(message, context);
  };
  
  const logInfo = (message: string, context?: ErrorContext) => {
    return errorManager.logInfo(message, context);
  };

  return {
    logError,
    logCritical,
    logWarning,
    logInfo,
    recoverFromError: errorManager.recoverFromError.bind(errorManager),
    getErrorStats: errorManager.getErrorStats.bind(errorManager),
    clearResolvedErrors: errorManager.clearResolvedErrors.bind(errorManager)
  };
};

// ================ CONVENIENCE FUNCTIONS ================

export const logError = (error: Error | string, context?: ErrorContext): string => {
  return ErrorManager.getInstance().logError(error, context);
};

export const logCritical = (error: Error | string, context?: ErrorContext): string => {
  return ErrorManager.getInstance().logCritical(error, context);
};

export const logWarning = (message: string, context?: ErrorContext): string => {
  return ErrorManager.getInstance().logWarning(message, context);
};

export const logInfo = (message: string, context?: ErrorContext): string => {
  return ErrorManager.getInstance().logInfo(message, context);
};

// Create and export singleton instance
export const errorManager = ErrorManager.getInstance();
